import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { hexToRgbTriplet } from '@/lib/format';
import { storage } from '@/lib/storage';

const DEFAULT_ACCENT_HEX = '#E25822';

interface ActivityScopeContextValue {
  activeActivityId: string | null;
  setActiveActivityId: (id: string | null) => void;
  accentHex: string;
  setAccent: (hex: string) => void;
}

const ActivityScopeContext = createContext<ActivityScopeContextValue | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export function ActivityScopeProvider({ children }: ProviderProps) {
  const [activeActivityId, setActiveActivityIdState] = useState<string | null>(() =>
    storage.get<string | null>('ember:activeActivityId', null),
  );
  const [accentHex, setAccent] = useState<string>(DEFAULT_ACCENT_HEX);

  useEffect(() => {
    const triplet = hexToRgbTriplet(accentHex);
    document.documentElement.style.setProperty('--accent-rgb', triplet);
  }, [accentHex]);

  const setActiveActivityId = (id: string | null): void => {
    setActiveActivityIdState(id);
    if (id) storage.set('ember:activeActivityId', id);
    else storage.remove('ember:activeActivityId');
  };

  const value = useMemo<ActivityScopeContextValue>(
    () => ({ activeActivityId, setActiveActivityId, accentHex, setAccent }),
    [activeActivityId, accentHex],
  );

  return <ActivityScopeContext.Provider value={value}>{children}</ActivityScopeContext.Provider>;
}

export function useActivityScope(): ActivityScopeContextValue {
  const ctx = useContext(ActivityScopeContext);
  if (!ctx) throw new Error('useActivityScope must be used within ActivityScopeProvider');
  return ctx;
}
